import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  FileSpreadsheet, Plus, Send, Eye, Pencil, Download, ScrollText, X, FlaskConical,
  CalendarClock, History, Power,
} from 'lucide-react'
import api from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'

type Report = {
  id: string
  name: string
  partner_label: string
  identifiers: string[]
  match_mode: 'contains' | 'exact'
  include_closed: boolean
  recipients: string[]
  cc: string[]
  frequency: 'weekly' | 'biweekly' | 'monthly'
  weekdays: number[]
  month_day: number | null
  send_time: string
  is_active: boolean
  next_run_at: string | null
  last_run_at: string | null
  edition_count: number
  schedule_label: string
}

type Run = {
  id: string
  report_id: string
  report_name: string | null
  kind: 'scheduled' | 'manual' | 'test'
  status: 'running' | 'success' | 'failed'
  edition: number | null
  recipients: string[]
  cc: string[]
  subject: string | null
  file_name: string | null
  file_size: number | null
  has_file: boolean
  rows_count: number | null
  accounts_count: number | null
  error: string | null
  started_at: string
}

type Log = { level: string; message: string; created_at: string }

// s|t|q|q|s|s|d — segunda a domingo, mesma ordem do backend (0 = segunda)
const DIAS = [
  { v: 0, curto: 'S', nome: 'Segunda' }, { v: 1, curto: 'T', nome: 'Terça' },
  { v: 2, curto: 'Q', nome: 'Quarta' }, { v: 3, curto: 'Q', nome: 'Quinta' },
  { v: 4, curto: 'S', nome: 'Sexta' }, { v: 5, curto: 'S', nome: 'Sábado' },
  { v: 6, curto: 'D', nome: 'Domingo' },
]

const FREQ: Record<string, string> = { weekly: 'Semanal', biweekly: 'Quinzenal', monthly: 'Mensal' }
const KIND: Record<string, string> = { scheduled: 'Agendado', manual: 'Manual', test: 'Teste' }
const STATUS: Record<string, { label: string; cls: string }> = {
  success: { label: 'Enviado', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  failed: { label: 'Falhou', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300' },
  running: { label: 'Enviando', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
}

const VAZIO: Omit<Report, 'id' | 'next_run_at' | 'last_run_at' | 'edition_count' | 'schedule_label'> = {
  name: '', partner_label: '', identifiers: [], match_mode: 'contains', include_closed: true,
  recipients: [], cc: [], frequency: 'weekly', weekdays: [4], month_day: 1, send_time: '08:00',
  is_active: true,
}

const dth = (v: string | null) =>
  v ? new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
const brl = (v: number | null | undefined) =>
  v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const erroMsg = (e: any) => {
  const d = e?.response?.data?.detail
  if (Array.isArray(d)) return d.map((x: any) => x.msg?.replace(/^Value error, /, '')).join(' · ')
  return d || e?.message || 'Erro inesperado'
}

const inputCls = 'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40'
const btn = 'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50'
const btnPrim = 'inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50'

/** Campo de lista (e-mails ou identificadores): Enter, vírgula ou ponto e vírgula adicionam. */
function ChipsInput({ value, onChange, placeholder, suggestions, listId }: {
  value: string[]; onChange: (v: string[]) => void; placeholder: string
  suggestions?: { value: string; leads: number }[]; listId?: string
}) {
  const [txt, setTxt] = useState('')
  const add = (raw: string) => {
    const novos = raw.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean)
    if (novos.length) onChange(Array.from(new Set([...value, ...novos])))
    setTxt('')
  }
  return (
    <div className="rounded-md border bg-background px-2 py-1.5 flex flex-wrap gap-1.5 items-center">
      {value.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 rounded bg-primary/10 text-primary px-2 py-0.5 text-xs">
          {v}
          <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} aria-label={`Remover ${v}`}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        list={listId}
        value={txt}
        onChange={(e) => {
          const t = e.target.value
          // escolher uma sugestão do datalist adiciona direto
          if (suggestions?.some((s) => s.value === t)) add(t)
          else setTxt(t)
        }}
        onKeyDown={(e) => {
          if (['Enter', ',', ';', 'Tab'].includes(e.key) && txt.trim()) { e.preventDefault(); add(txt) }
          if (e.key === 'Backspace' && !txt && value.length) onChange(value.slice(0, -1))
        }}
        onBlur={() => txt.trim() && add(txt)}
        placeholder={value.length ? '' : placeholder}
        className="flex-1 min-w-[160px] bg-transparent text-sm outline-none py-0.5"
      />
      {listId && suggestions && (
        <datalist id={listId}>
          {suggestions.map((s) => <option key={s.value} value={s.value}>{s.leads} lead(s)</option>)}
        </datalist>
      )}
    </div>
  )
}

function Modal({ titulo, onClose, children, largo }: { titulo: string; onClose: () => void; children: React.ReactNode; largo?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className={`bg-card rounded-lg border shadow-xl w-full ${largo ? 'max-w-6xl' : 'max-w-2xl'} my-8`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h2 className="font-semibold">{titulo}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted" aria-label="Fechar"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

export default function PartnerReportsPage() {
  const user = useAuthStore((s) => s.user)
  const [aba, setAba] = useState<'reports' | 'historico'>('reports')
  const [reports, setReports] = useState<Report[]>([])
  const [runs, setRuns] = useState<Run[]>([])
  const [filtroReport, setFiltroReport] = useState('')
  const [sugestoes, setSugestoes] = useState<{ value: string; leads: number }[]>([])
  const [carregando, setCarregando] = useState(true)
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'erro'; msg: string } | null>(null)

  const [form, setForm] = useState<typeof VAZIO & { id?: string }>(VAZIO)
  const [editando, setEditando] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [erroForm, setErroForm] = useState('')

  const [preview, setPreview] = useState<{ rep: Report; data: any } | null>(null)
  const [teste, setTeste] = useState<{ rep: Report; emails: string[] } | null>(null)
  const [enviando, setEnviando] = useState('')
  const [logs, setLogs] = useState<{ run: Run; itens: Log[] } | null>(null)

  const carregar = useCallback(() => {
    setCarregando(true)
    Promise.all([
      api.get('/v1/partner-reports/'),
      api.get('/v1/partner-reports/runs', { params: { per_page: 200, report_id: filtroReport || undefined } }),
    ])
      .then(([r, h]) => { setReports(r.data.items || []); setRuns(h.data.items || []) })
      .finally(() => setCarregando(false))
  }, [filtroReport])

  useEffect(() => { carregar() }, [carregar])
  useEffect(() => { api.get('/v1/partner-reports/identifiers').then(({ data }) => setSugestoes(data || [])) }, [])

  const flash = (tipo: 'ok' | 'erro', msg: string) => { setAviso({ tipo, msg }); setTimeout(() => setAviso(null), 7000) }

  const abrirNovo = () => { setForm(VAZIO); setErroForm(''); setEditando(true) }
  const abrirEdicao = (r: Report) => {
    setForm({ ...r, month_day: r.month_day ?? 1, weekdays: r.weekdays?.length ? r.weekdays : [4] })
    setErroForm(''); setEditando(true)
  }

  const salvar = async () => {
    setSalvando(true); setErroForm('')
    const { id, ...body } = form as any
    delete body.next_run_at; delete body.last_run_at; delete body.edition_count; delete body.schedule_label
    delete body.created_at; delete body.updated_at; delete body.anchor_date
    try {
      if (id) await api.put(`/v1/partner-reports/${id}`, body)
      else await api.post('/v1/partner-reports/', body)
      setEditando(false); flash('ok', 'Report salvo.'); carregar()
    } catch (e) { setErroForm(erroMsg(e)) } finally { setSalvando(false) }
  }

  const alternarAtivo = async (r: Report) => {
    const { id, next_run_at, last_run_at, edition_count, schedule_label, ...resto } = r as any
    delete resto.created_at; delete resto.updated_at; delete resto.anchor_date
    try {
      await api.put(`/v1/partner-reports/${id}`, { ...resto, is_active: !r.is_active })
      flash('ok', r.is_active ? `"${r.name}" pausado — nenhum envio agendado.` : `"${r.name}" ativado.`)
      carregar()
    } catch (e) { flash('erro', erroMsg(e)) }
  }

  const abrirPreview = async (r: Report) => {
    try { const { data } = await api.get(`/v1/partner-reports/${r.id}/preview`); setPreview({ rep: r, data }) }
    catch (e) { flash('erro', erroMsg(e)) }
  }

  const enviar = async (r: Report, test: boolean, emails: string[] = []) => {
    if (!test && !confirm(`Enviar agora "${r.name}" para ${[...r.recipients, ...r.cc].join(', ')}?\nO envio numera uma edição oficial.`)) return
    setEnviando(r.id)
    try {
      const { data } = await api.post(`/v1/partner-reports/${r.id}/send`, { test, recipients: emails }, { timeout: 120000 })
      if (data.status === 'success') flash('ok', `${test ? 'Teste enviado' : 'Report enviado'} para ${data.recipients.join(', ')} · ${data.rows_count} linha(s).`)
      else flash('erro', `Falhou: ${data.error}`)
      setTeste(null); carregar()
    } catch (e) { flash('erro', erroMsg(e)) } finally { setEnviando('') }
  }

  const baixar = async (x: Run) => {
    try {
      const { data } = await api.get(`/v1/partner-reports/runs/${x.id}/download`, { responseType: 'blob' })
      const url = URL.createObjectURL(data)
      const a = document.createElement('a')
      a.href = url; a.download = x.file_name || 'cockpit.xlsx'; a.click()
      URL.revokeObjectURL(url)
    } catch (e) { flash('erro', 'Planilha indisponível para este envio.') }
  }

  const verLogs = async (x: Run) => {
    const { data } = await api.get(`/v1/partner-reports/runs/${x.id}/logs`)
    setLogs({ run: x, itens: data })
  }

  const toggleDia = (d: number) =>
    setForm((f) => ({ ...f, weekdays: f.weekdays.includes(d) ? f.weekdays.filter((x) => x !== d) : [...f.weekdays, d].sort() }))

  const resumoAgenda = useMemo(() => {
    if (form.frequency === 'monthly') return `Todo mês, ${form.month_day ? `no dia ${form.month_day}` : 'no último dia'}, às ${form.send_time}`
    const dias = DIAS.filter((d) => form.weekdays.includes(d.v)).map((d) => d.nome.toLowerCase())
    return `${form.frequency === 'biweekly' ? 'A cada duas semanas' : 'Toda semana'}, ${dias.join(', ') || '(escolha os dias)'}, às ${form.send_time}`
  }, [form])

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6" /> Reports de Parceiros
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Cockpit Operacional enviado por e-mail com planilha anexa, listando as contas marcadas no
            campo <b>BDR/Parceiro/Indicação</b> com os identificadores de cada cadastro. Cada envio fica
            no histórico com a planilha para download e o log completo.
          </p>
        </div>
        <button onClick={abrirNovo} className={btnPrim}><Plus className="w-4 h-4" /> Novo report</button>
      </div>

      {aviso && (
        <div className={`rounded-md border px-4 py-2.5 text-sm ${aviso.tipo === 'ok'
          ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
          : 'border-rose-300 bg-rose-50 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200'}`}>{aviso.msg}</div>
      )}

      <div className="flex gap-2 border-b">
        {([['reports', 'Reports', CalendarClock], ['historico', 'Histórico de envios', History]] as const).map(([k, rot, Ic]) => (
          <button key={k} onClick={() => setAba(k)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 inline-flex items-center gap-2 ${aba === k ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <Ic className="w-4 h-4" /> {rot}
          </button>
        ))}
      </div>

      {aba === 'reports' && (
        <div className="rounded-lg border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2.5">Report</th>
                <th className="text-left px-4 py-2.5">BDR/Parceiro/Indicação</th>
                <th className="text-left px-4 py-2.5">Destinatários</th>
                <th className="text-left px-4 py-2.5">Agenda</th>
                <th className="text-left px-4 py-2.5">Próximo envio</th>
                <th className="text-right px-4 py-2.5">Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Carregando…</td></tr>}
              {!carregando && !reports.length && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Nenhum report cadastrado.</td></tr>
              )}
              {reports.map((r) => (
                <tr key={r.id} className="border-t align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground">Título: Cockpit Operacional | {r.partner_label} · {r.edition_count} edição(ões)</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {r.identifiers.map((i) => <span key={i} className="rounded bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 px-2 py-0.5 text-xs">{i}</span>)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{r.match_mode === 'exact' ? 'valor exato' : 'contém o texto'}{r.include_closed ? ' · inclui ganhas/perdidas' : ' · só em aberto'}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {r.recipients.join(', ')}
                    {r.cc.length > 0 && <div className="text-muted-foreground">cc: {r.cc.join(', ')}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{r.schedule_label}</td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    {r.is_active
                      ? dth(r.next_run_at)
                      : <span className="rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 px-2 py-0.5">Pausado</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      <button className={btn} onClick={() => abrirPreview(r)}><Eye className="w-3.5 h-3.5" /> Prévia</button>
                      <button className={btn} disabled={enviando === r.id}
                        onClick={() => setTeste({ rep: r, emails: user?.email ? [user.email] : [] })}>
                        <FlaskConical className="w-3.5 h-3.5" /> Teste</button>
                      <button className={btn} disabled={enviando === r.id} onClick={() => enviar(r, false)}>
                        <Send className="w-3.5 h-3.5" /> {enviando === r.id ? 'Enviando…' : 'Enviar agora'}</button>
                      <button className={btn} onClick={() => { setFiltroReport(r.id); setAba('historico') }}><History className="w-3.5 h-3.5" /> Histórico</button>
                      <button className={btn} onClick={() => abrirEdicao(r)}><Pencil className="w-3.5 h-3.5" /> Editar</button>
                      <button className={btn} onClick={() => alternarAtivo(r)}><Power className="w-3.5 h-3.5" /> {r.is_active ? 'Pausar' : 'Ativar'}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {aba === 'historico' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <select value={filtroReport} onChange={(e) => setFiltroReport(e.target.value)} className={`${inputCls} max-w-xs`}>
              <option value="">Todos os reports</option>
              {reports.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <span className="text-xs text-muted-foreground">{runs.length} envio(s)</span>
          </div>
          <div className="rounded-lg border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5">Data</th>
                  <th className="text-left px-4 py-2.5">Report</th>
                  <th className="text-left px-4 py-2.5">Tipo</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5">Destinatários</th>
                  <th className="text-right px-4 py-2.5">Contas · linhas</th>
                  <th className="text-right px-4 py-2.5">Planilha / log</th>
                </tr>
              </thead>
              <tbody>
                {!runs.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Nenhum envio registrado.</td></tr>}
                {runs.map((x) => (
                  <tr key={x.id} className="border-t align-top">
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{dth(x.started_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm">{x.report_name || '—'}</div>
                      {x.edition && <div className="text-xs text-muted-foreground">Edição nº {String(x.edition).padStart(3, '0')}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">{KIND[x.kind] || x.kind}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded px-2 py-0.5 text-xs ${STATUS[x.status]?.cls}`}>{STATUS[x.status]?.label || x.status}</span>
                      {x.error && <div className="text-xs text-rose-600 mt-1 max-w-xs">{x.error}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">{x.recipients.join(', ')}{x.cc.length > 0 && <div className="text-muted-foreground">cc: {x.cc.join(', ')}</div>}</td>
                    <td className="px-4 py-3 text-xs text-right tabular-nums">{x.accounts_count ?? '—'} · {x.rows_count ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5 justify-end">
                        <button className={btn} disabled={!x.has_file} onClick={() => baixar(x)} title={x.file_name || ''}>
                          <Download className="w-3.5 h-3.5" /> XLSX{x.file_size ? ` · ${Math.round(x.file_size / 1024)} KB` : ''}</button>
                        <button className={btn} onClick={() => verLogs(x)}><ScrollText className="w-3.5 h-3.5" /> Log</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editando && (
        <Modal titulo={form.id ? 'Editar report' : 'Novo report'} onClose={() => setEditando(false)}>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="space-y-1"><span className="text-sm font-medium">Nome do report *</span>
                <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex.: Cockpit Operacional · BDR BS4IT" /></label>
              <label className="space-y-1"><span className="text-sm font-medium">Rótulo no título da planilha *</span>
                <input className={inputCls} value={form.partner_label} onChange={(e) => setForm({ ...form, partner_label: e.target.value })} placeholder="Ex.: BS4IT" /></label>
            </div>
            <div className="space-y-1">
              <span className="text-sm font-medium">Nome do BDR/Parceiro/Indicação *</span>
              <ChipsInput value={form.identifiers} onChange={(v) => setForm({ ...form, identifiers: v })}
                placeholder="Digite e tecle Enter — ex.: BS4IT" suggestions={sugestoes} listId="pr-idents" />
              <div className="flex gap-4 text-xs text-muted-foreground pt-1 flex-wrap">
                <label className="inline-flex items-center gap-1.5"><input type="radio" checked={form.match_mode === 'contains'} onChange={() => setForm({ ...form, match_mode: 'contains' })} /> Contém o texto</label>
                <label className="inline-flex items-center gap-1.5"><input type="radio" checked={form.match_mode === 'exact'} onChange={() => setForm({ ...form, match_mode: 'exact' })} /> Valor exato</label>
                <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={form.include_closed} onChange={(e) => setForm({ ...form, include_closed: e.target.checked })} /> Incluir ganhas e perdidas</label>
              </div>
            </div>
            <div className="space-y-1"><span className="text-sm font-medium">Destinatários *</span>
              <ChipsInput value={form.recipients} onChange={(v) => setForm({ ...form, recipients: v })} placeholder="email@parceiro.com.br — Enter para adicionar" /></div>
            <div className="space-y-1"><span className="text-sm font-medium">Cópia (cc)</span>
              <ChipsInput value={form.cc} onChange={(v) => setForm({ ...form, cc: v })} placeholder="opcional" /></div>

            <div className="rounded-md border p-3 space-y-3">
              <div className="flex gap-2 flex-wrap">
                {(['weekly', 'biweekly', 'monthly'] as const).map((f) => (
                  <button key={f} type="button" onClick={() => setForm({ ...form, frequency: f })}
                    className={`px-3 py-1.5 rounded-md text-sm border ${form.frequency === f ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>{FREQ[f]}</button>
                ))}
              </div>
              {form.frequency !== 'monthly' ? (
                <div className="flex gap-1.5">
                  {DIAS.map((d) => (
                    <button key={d.v} type="button" title={d.nome} onClick={() => toggleDia(d.v)}
                      className={`w-9 h-9 rounded-full text-sm font-medium border ${form.weekdays.includes(d.v) ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>{d.curto}</button>
                  ))}
                </div>
              ) : (
                <label className="flex items-center gap-2 text-sm">Dia do mês
                  <input type="number" min={0} max={31} className={`${inputCls} w-24`} value={form.month_day ?? 1}
                    onChange={(e) => setForm({ ...form, month_day: Number(e.target.value) })} />
                  <span className="text-xs text-muted-foreground">0 = último dia · meses curtos usam o último dia</span>
                </label>
              )}
              <label className="flex items-center gap-2 text-sm">Horário (Brasília)
                <input type="time" className={`${inputCls} w-32`} value={form.send_time} onChange={(e) => setForm({ ...form, send_time: e.target.value })} /></label>
              <div className="text-xs text-muted-foreground">{resumoAgenda}</div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Ativo — envia automaticamente na agenda
            </label>
            {erroForm && <div className="text-sm text-rose-600">{erroForm}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button className={btn} onClick={() => setEditando(false)}>Cancelar</button>
              <button className={btnPrim} disabled={salvando} onClick={salvar}>{salvando ? 'Salvando…' : 'Salvar'}</button>
            </div>
          </div>
        </Modal>
      )}

      {teste && (
        <Modal titulo={`Enviar teste · ${teste.rep.name}`} onClose={() => setTeste(null)}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">O teste vai só para os e-mails abaixo, com o assunto marcado como [TESTE].
              Não numera edição e não altera a agenda.</p>
            <ChipsInput value={teste.emails} onChange={(v) => setTeste({ ...teste, emails: v })} placeholder="e-mail — Enter para adicionar" />
            <div className="flex justify-end gap-2">
              <button className={btn} onClick={() => setTeste(null)}>Cancelar</button>
              <button className={btnPrim} disabled={!teste.emails.length || enviando === teste.rep.id}
                onClick={() => enviar(teste.rep, true, teste.emails)}>
                <FlaskConical className="w-4 h-4" /> {enviando === teste.rep.id ? 'Enviando…' : 'Enviar teste'}</button>
            </div>
          </div>
        </Modal>
      )}

      {preview && (
        <Modal largo titulo={`Prévia · Cockpit Operacional | ${preview.rep.partner_label}`} onClose={() => setPreview(null)}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {[['Contas', preview.data.summary.contas], ['Oportunidades abertas', preview.data.summary.oportunidades_abertas],
              ['Pipeline aberto', brl(preview.data.summary.pipeline_aberto)], ['Pipeline ponderado', brl(preview.data.summary.pipeline_ponderado)]]
              .map(([k, v]: any) => (
                <div key={k} className="rounded-lg border p-3"><div className="text-lg font-semibold tabular-nums">{v}</div><div className="text-xs text-muted-foreground">{k}</div></div>
              ))}
          </div>
          <div className="overflow-x-auto max-h-[60vh] rounded border">
            <table className="w-full text-xs">
              <thead className="sticky top-0" style={{ background: '#00284A', color: '#D4A853' }}>
                <tr>{['ID', 'Organização', 'Título', 'Executivo(a)', 'Status', 'Dias criação', 'Dias últ. ativ.', 'Etapa', 'Q criação', 'Q fecham.', 'Prob.', 'Valor']
                  .map((h) => <th key={h} className="px-2 py-2 text-left whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {preview.data.rows.map((r: any, i: number) => (
                  <tr key={r.id + i} className="border-t odd:bg-muted/30">
                    <td className="px-2 py-1.5 whitespace-nowrap">{r.id}</td><td className="px-2 py-1.5">{r.org}</td>
                    <td className="px-2 py-1.5">{r.titulo}</td><td className="px-2 py-1.5 whitespace-nowrap">{r.executivo}</td>
                    <td className="px-2 py-1.5">{r.status}</td><td className="px-2 py-1.5 text-center">{r.dias_criacao ?? ''}</td>
                    <td className="px-2 py-1.5 text-center">{r.dias_atividade ?? ''}</td><td className="px-2 py-1.5 whitespace-nowrap">{r.etapa}</td>
                    <td className="px-2 py-1.5">{r.q_criacao}</td><td className="px-2 py-1.5">{r.q_fecham}</td>
                    <td className="px-2 py-1.5 text-center">{r.prob ?? ''}</td><td className="px-2 py-1.5 text-right whitespace-nowrap">{r.valor != null ? brl(r.valor) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {logs && (
        <Modal titulo={`Log do envio · ${dth(logs.run.started_at)}`} onClose={() => setLogs(null)}>
          <div className="space-y-1 font-mono text-xs">
            {!logs.itens.length && <div className="text-muted-foreground">Sem registros.</div>}
            {logs.itens.map((l, i) => (
              <div key={i} className={`flex gap-3 ${l.level === 'error' ? 'text-rose-600' : l.level === 'warning' ? 'text-amber-600' : ''}`}>
                <span className="text-muted-foreground shrink-0">{new Date(l.created_at).toLocaleTimeString('pt-BR')}</span>
                <span className="uppercase w-14 shrink-0">{l.level}</span>
                <span className="break-all">{l.message}</span>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  )
}
